import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, phpFmt } from "../lib/api";
import ProtectedRoute from "../components/ProtectedRoute";
import { useAuth, formatApiError } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { MapPin, Receipt, ArrowRight, FileText, Banknote, Save, UserCog } from "lucide-react";

function ProfileCard() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState({
        full_name: user?.full_name || "",
        phone: user?.phone || "",
        company_name: user?.company_name || "",
        industry: user?.industry || "",
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/client/profile", form);
            setUser((u) => ({ ...u, ...data }));
            toast.success("Profile saved");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    return (
        <div data-testid="profile-card" className="border border-zinc-200 bg-white">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <UserCog className="h-4 w-4" strokeWidth={1.5} />
                    <div className="font-display text-lg font-semibold">My Business Profile</div>
                </div>
                <div className="text-[10px] uppercase tracking-label text-zinc-500">Auto-fills contracts</div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Full name</label>
                    <input data-testid="profile-full-name" value={form.full_name}
                        onChange={(e) => u("full_name", e.target.value)}
                        className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none text-sm" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Phone</label>
                    <input data-testid="profile-phone" value={form.phone}
                        onChange={(e) => u("phone", e.target.value)}
                        className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none text-sm" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Registered business name</label>
                    <input data-testid="profile-company" value={form.company_name}
                        onChange={(e) => u("company_name", e.target.value)}
                        placeholder="e.g. Acme Import Export Corp"
                        className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none text-sm" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-label text-zinc-500">Line of business / industry</label>
                    <input data-testid="profile-industry" value={form.industry}
                        onChange={(e) => u("industry", e.target.value)}
                        placeholder="e.g. IT Consulting"
                        className="mt-1 w-full border border-zinc-300 px-3 py-2.5 focus:border-zinc-950 focus:outline-none text-sm" />
                </div>
                <div className="md:col-span-2 flex items-center justify-end">
                    <button data-testid="profile-save-btn" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-1.5 bg-zinc-950 text-white px-5 py-2.5 text-sm hover:bg-[#FF6A13] transition-colors disabled:opacity-40">
                        <Save className="h-4 w-4" strokeWidth={1.5} /> {saving ? "Saving…" : "Save profile"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Body() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/client/agreements").then((r) => setRows(r.data)).finally(() => setLoading(false));
    }, []);

    return (
        <div className="pt-24 min-h-screen">
            <Toaster richColors position="top-center" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-label text-zinc-500">Seeker Dashboard</div>
                        <h1 className="font-display text-5xl font-semibold tracking-tighter mt-2">My Workspace</h1>
                    </div>
                    <Link to="/search" data-testid="browse-more-link" className="bg-zinc-950 text-white px-5 py-3 text-sm hover:bg-[#FF6A13] transition-colors">Browse addresses</Link>
                </div>

                <div className="mt-10">
                    <ProfileCard />
                </div>

                <div className="mt-10">
                    <div className="text-[10px] uppercase tracking-label text-zinc-500">Agreements</div>
                    <h2 className="font-display text-3xl font-semibold tracking-tight mt-2">My Agreements</h2>
                </div>

                <div className="mt-6">
                    {loading ? (
                        <div className="text-zinc-500 text-sm tracking-label uppercase">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div data-testid="empty-agreements" className="border border-zinc-200 bg-zinc-50 p-12 text-center">
                            <div className="font-display text-2xl font-semibold">No agreements yet.</div>
                            <p className="mt-2 text-sm text-zinc-600">Pick a city and claim an address your clients will remember.</p>
                            <Link to="/search" className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-3 text-sm hover:bg-[#FF6A13] transition-colors">
                                Browse addresses <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
                            {rows.map((s) => (
                                <div key={s.agreement_id} data-testid={`agr-${s.agreement_id}`} className="bg-white p-6">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500 flex items-center gap-1.5">
                                                <MapPin className="h-3 w-3" strokeWidth={1.5} /> {s.center_city}
                                            </div>
                                            <div className="font-display text-2xl font-semibold tracking-tight mt-1">{s.center_name}</div>
                                            <div className="text-xs text-zinc-500 mt-0.5">{s.center_public_area}</div>
                                        </div>
                                        <span className={`text-[10px] uppercase tracking-label px-2 py-1 ${s.status === "active" ? "bg-green-100 text-green-800" : "bg-zinc-100 text-zinc-700"}`}>{s.status}</span>
                                    </div>
                                    <div className="mt-5 grid grid-cols-3 gap-px bg-zinc-200">
                                        <div className="bg-white p-3">
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500">Rate</div>
                                            <div className="font-display font-semibold">{phpFmt(s.monthly_rate_php)}</div>
                                            <div className="text-[10px] text-zinc-500">per month</div>
                                        </div>
                                        <div className="bg-white p-3">
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500">Payment</div>
                                            <div className="font-display font-semibold flex items-center gap-1">
                                                {s.payment_mode === "pdc" ? (<><FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> 12 PDCs</>) : (<><Banknote className="h-3.5 w-3.5" strokeWidth={1.5} /> Cash</>)}
                                            </div>
                                            <div className="text-[10px] text-zinc-500">{s.months} months</div>
                                        </div>
                                        <div className="bg-white p-3">
                                            <div className="text-[10px] uppercase tracking-label text-zinc-500">Total</div>
                                            <div className="font-display font-semibold">{phpFmt(s.total_amount_php)}</div>
                                        </div>
                                    </div>
                                    <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500">
                                        <Receipt className="h-3.5 w-3.5" strokeWidth={1.5} /> Agreement ID: {s.agreement_id}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function ClientDashboard() {
    return (
        <ProtectedRoute roles={["client", "admin"]}>
            <Body />
        </ProtectedRoute>
    );
}
